// #959 / #960 — shared "News Crawler searches" panel.
//
// Reused by:
//   - AdminCronNewsCrawler.js (global view, /admin/crons/news-crawler)
//   - AdminUserNewsTopics.js (per-user view, /admin/users/<id>/news-topics)
//
// Renders a cost header + a paginated searches grid. The panel is
// scope-agnostic — it just reads `cost` / `searches` / `has_more`
// from props. The parent component handles fetching + pagination
// state. Splitting the shared shape here avoids the cosmetic drift
// the issue body (#960) warned about.
import React from 'react';
import { Link } from 'react-router-dom';
import { fmtDateTime } from './userSubPageStyles';

function fmtUSD(v) {
  if (v == null) return '—';
  // 4-decimal precision keeps fractional-penny figures readable.
  return `$${Number(v).toFixed(4)}`;
}

function CostHeader({ cost }) {
  if (!cost) return null;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 12, marginBottom: 16,
    }}>
      {[
        ['Total calls',       cost.total_calls,       null],
        ['Total USD',         null,                   cost.total_usd],
        ['This-month calls',  cost.this_month_calls,  null],
        ['This-month USD',    null,                   cost.this_month_usd],
      ].map(([label, count, usd]) => (
        <div key={label} style={{
          background: '#FAF8F5', borderRadius: 8, padding: '12px 16px',
        }}>
          <div style={{
            color: '#888', fontSize: '0.72rem', textTransform: 'uppercase',
            letterSpacing: '0.05em', fontWeight: 600,
          }}>
            {label}
          </div>
          <div style={{
            fontSize: '1.4rem', fontWeight: 700, marginTop: 4,
            color: '#2C2C2C', fontVariantNumeric: 'tabular-nums',
          }}>
            {count != null ? count.toLocaleString() : fmtUSD(usd)}
          </div>
        </div>
      ))}
      <div style={{
        background: '#FAF8F5', borderRadius: 8, padding: '12px 16px',
        gridColumn: '1 / -1', color: '#666', fontSize: '0.78rem',
      }}>
        Provider: <strong>{cost.provider}</strong> &middot; per-call
        cost: <strong>{fmtUSD(cost.per_call_usd)}</strong> (env-overridable
        via <code>SEARCH_PROVIDER_PRICES_JSON</code>).
      </div>
    </div>
  );
}

export default function NewsCrawlerSearchesPanel({
  cost, searches, hasMore, onLoadMore,
  showUserColumn = false,
}) {
  return (
    <>
      <CostHeader cost={cost} />

      {(!searches || searches.length === 0) ? (
        <div className="empty-state">
          No cached searches yet. The next 03:00 UTC cron will
          populate this when there are eligible topics.
        </div>
      ) : (
        <>
          <div className="data-grid">
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  {showUserColumn && <th>User</th>}
                  <th>Topic</th>
                  <th>Query</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {searches.map(s => (
                  <tr key={s.id}>
                    <td style={{
                      color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap',
                    }}>
                      {fmtDateTime(s.executed_at)}
                    </td>
                    {showUserColumn && (
                      <td>
                        {s.user_id ? (
                          <Link
                            to={`/admin/users/${s.user_id}/news-topics`}
                            style={{
                              color: '#E8845A',
                              fontFamily: 'ui-monospace, monospace',
                              fontSize: '0.78rem',
                            }}
                          >
                            {s.user_id.slice(0, 8)} →
                          </Link>
                        ) : (
                          <span style={{ color: '#aaa' }}>—</span>
                        )}
                      </td>
                    )}
                    <td style={{ fontWeight: 600, color: '#E8845A' }}>
                      {s.topic}
                    </td>
                    <td style={{
                      color: '#888', fontSize: '0.82rem',
                      fontFamily: 'ui-monospace, monospace',
                    }}>
                      {s.search_query}
                    </td>
                    <td style={{
                      color: '#666', fontSize: '0.85rem',
                      maxWidth: 480,
                    }}>
                      {s.summary || <em style={{ color: '#aaa' }}>—</em>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={onLoadMore}
                style={{
                  padding: '7px 14px', borderRadius: 6,
                  border: '1px solid #DDD', background: '#fff',
                  color: '#E8845A', fontSize: '0.85rem', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Load 100 more
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
